import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { Mic, Video, Square } from 'lucide-react';

const DescriptiveQASection = ({ simulatorState }) => {
  const [selectedQuestionType, setSelectedQuestionType] = useState('behavioral');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [recordingMode, setRecordingMode] = useState('audio'); // audio, video, text
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [responses, setResponses] = useState([]);
  const [textResponse, setTextResponse] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);

  const questionCategories = [
    {
      id: 'behavioral',
      name: 'Behavioral Questions',
      icon: '🧠',
      description: 'STAR method, leadership, conflict resolution',
      count: 67
    },
    {
      id: 'technical',
      name: 'Technical Deep Dive',
      icon: '⚙️',
      description: 'System design, architecture, problem-solving',
      count: 89
    },
    {
      id: 'situational',
      name: 'Situational Scenarios',
      icon: '🎯',
      description: 'Real-world testing scenarios and challenges',
      count: 45
    },
    {
      id: 'cultural',
      name: 'Cultural Fit',
      icon: '🤝',
      description: 'Company values, team dynamics, motivation',
      count: 34
    }
  ];

  const sampleQuestions = [
    {
      id: 1,
      category: 'behavioral',
      question: 'Describe a time when you had to deal with a critical bug found just before a major release. How did you handle the situation?',
      followUp: 'What would you do differently if faced with a similar situation?',
      timeLimit: 180, // 3 minutes
      difficulty: 'Medium',
      tips: ['Use STAR method', 'Focus on your specific actions', 'Highlight communication skills']
    },
    {
      id: 2,
      category: 'technical',
      question: 'Explain how you would design a comprehensive test strategy for a microservices architecture with multiple databases.',
      followUp: 'How would you handle data consistency testing across services?',
      timeLimit: 300, // 5 minutes
      difficulty: 'Advanced',
      tips: ['Consider integration testing', 'Discuss contract testing', 'Address data flow']
    }
  ];

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      stopRecording();
      if (streamRef?.current) {
        streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const constraints = recordingMode === 'video' 
        ? { video: true, audio: true }
        : { audio: true };

      const stream = await navigator.mediaDevices?.getUserMedia(constraints);
      streamRef.current = stream;

      if (recordingMode === 'video' && videoRef?.current) {
        videoRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        chunks?.push(event?.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { 
          type: recordingMode === 'video' ? 'video/webm' : 'audio/webm' 
        });
        const url = URL.createObjectURL(blob);
        
        setResponses(prev => [...prev, {
          questionId: currentQuestion,
          type: recordingMode,
          url,
          duration: recordingTime,
          timestamp: new Date()?.toISOString()
        }]);
      };

      mediaRecorder?.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone/camera. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef?.current && isRecording) {
      mediaRecorderRef?.current?.stop();
      setIsRecording(false);
    }

    if (intervalRef?.current) {
      clearInterval(intervalRef?.current);
    }

    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
    }

    if (videoRef?.current) {
      videoRef.current.srcObject = null;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const handleTextSubmit = () => {
    if (textResponse?.trim()) {
      setResponses(prev => [...prev, {
        questionId: currentQuestion,
        type: 'text',
        content: textResponse,
        wordCount: textResponse?.split(' ')?.length,
        timestamp: new Date()?.toISOString()
      }]);
      setTextResponse('');
      // Generate AI analysis for text response
      generateAIAnalysis(textResponse);
    }
  };

  const generateAIAnalysis = async (content) => {
    // Mock AI analysis - in real implementation, integrate with OpenAI
    setTimeout(() => {
      setAiAnalysis({
        clarity: 8.5,
        structure: 9.0,
        relevance: 8.0,
        confidence: 7.5,
        suggestions: [
          'Excellent use of specific examples',
          'Consider adding more quantitative results',
          'Good clear communication style'
        ],
        keyStrengths: ['Clear articulation', 'Relevant experience', 'Problem-solving approach'],
        improvements: ['Add more metrics', 'Expand on lessons learned']
      });
    }, 2000);
  };

  const nextQuestion = () => {
    if (currentQuestion < sampleQuestions?.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setAiAnalysis(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Question Category Selection */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {questionCategories?.map((category) => (
          <div
            key={category?.id}
            onClick={() => setSelectedQuestionType(category?.id)}
            className={`
              p-4 rounded-lg border-2 cursor-pointer transition-all duration-300 hover:shadow-md
              ${selectedQuestionType === category?.id
                ? 'border-indigo-500 bg-indigo-50 shadow-md'
                : 'border-gray-200 hover:border-indigo-300'
              }
            `}
          >
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-2xl">{category?.icon}</span>
              <div>
                <h3 className="font-semibold text-gray-800">{category?.name}</h3>
                <p className="text-xs text-gray-500">{category?.count} questions</p>
              </div>
            </div>
            <p className="text-sm text-gray-600">{category?.description}</p>
          </div>
        ))}
      </div>
      {/* Response Mode Selection */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Choose Response Method</h3>
        <div className="flex space-x-4">
          <button
            onClick={() => setRecordingMode('audio')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              recordingMode === 'audio' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <Mic className="w-4 h-4" />
            <span>Audio Recording</span>
          </button>
          <button
            onClick={() => setRecordingMode('video')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              recordingMode === 'video' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <Video className="w-4 h-4" />
            <span>Video Recording</span>
          </button>
          <button
            onClick={() => setRecordingMode('text')}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              recordingMode === 'text' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <span>📝</span>
            <span>Text Response</span>
          </button>
        </div>
      </div>
      {/* Question Interface */}
      <div className="bg-white rounded-xl shadow-lg p-8">
        {/* Question Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">
              Question {currentQuestion + 1} of {sampleQuestions?.length}
            </h2>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {sampleQuestions?.[currentQuestion]?.difficulty}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                {formatTime(sampleQuestions?.[currentQuestion]?.timeLimit || 180)} limit
              </span>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <p className="text-gray-800 text-lg leading-relaxed">
              {sampleQuestions?.[currentQuestion]?.question}
            </p>
            {sampleQuestions?.[currentQuestion]?.followUp && (
              <p className="text-gray-600 mt-3 italic">
                Follow-up: {sampleQuestions?.[currentQuestion]?.followUp}
              </p>
            )}
          </div>

          {/* Tips */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">💡 Tips for a great answer:</h4>
            <ul className="list-disc list-inside space-y-1">
              {sampleQuestions?.[currentQuestion]?.tips?.map((tip, index) => (
                <li key={index} className="text-blue-700 text-sm">{tip}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recording Interface */}
        {(recordingMode === 'audio' || recordingMode === 'video') && (
          <div className="space-y-4">
            {/* Video Preview */}
            {recordingMode === 'video' && (
              <div className="flex justify-center">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  className="w-64 h-48 bg-gray-200 rounded-lg"
                />
              </div>
            )}

            {/* Recording Controls */}
            <div className="flex justify-center items-center space-x-4">
              <div className="text-2xl font-mono text-gray-700">
                {formatTime(recordingTime)}
              </div>
              
              {!isRecording ? (
                <Button onClick={startRecording} className="bg-red-500 hover:bg-red-600">
                  <Mic className="w-4 h-4 mr-2" />
                  Start Recording
                </Button>
              ) : (
                <Button onClick={stopRecording} variant="outline">
                  <Square className="w-4 h-4 mr-2" />
                  Stop Recording
                </Button>
              )}
            </div>

            {/* Recording Status */}
            {isRecording && (
              <div className="flex justify-center">
                <div className="flex items-center space-x-2 text-red-600">
                  <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                  <span>Recording in progress...</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Text Response Interface */}
        {recordingMode === 'text' && (
          <div className="space-y-4">
            <textarea
              value={textResponse}
              onChange={(e) => setTextResponse(e?.target?.value)}
              placeholder="Type your response here... Aim for detailed, structured answers."
              className="w-full h-40 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Word count: {textResponse?.split(' ')?.filter(word => word)?.length}
              </div>
              <Button onClick={handleTextSubmit} disabled={!textResponse?.trim()}>
                Submit Response
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* AI Analysis Results */}
      {aiAnalysis && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-6 text-green-700">🤖 AI Analysis Results</h3>
          
          {/* Scoring */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{aiAnalysis?.clarity}</div>
              <div className="text-sm text-green-700">Clarity</div>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{aiAnalysis?.structure}</div>
              <div className="text-sm text-blue-700">Structure</div>
            </div>
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{aiAnalysis?.relevance}</div>
              <div className="text-sm text-purple-700">Relevance</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">{aiAnalysis?.confidence}</div>
              <div className="text-sm text-orange-700">Confidence</div>
            </div>
          </div>

          {/* Detailed Feedback */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-green-700 mb-3">✅ Key Strengths</h4>
              <ul className="space-y-2">
                {aiAnalysis?.keyStrengths?.map((strength, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-green-600">•</span>
                    <span className="text-gray-700">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-orange-700 mb-3">🎯 Areas for Improvement</h4>
              <ul className="space-y-2">
                {aiAnalysis?.improvements?.map((improvement, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-orange-600">•</span>
                    <span className="text-gray-700">{improvement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Suggestions */}
          <div className="mt-6">
            <h4 className="font-semibold text-blue-700 mb-3">💡 Specific Suggestions</h4>
            <div className="space-y-2">
              {aiAnalysis?.suggestions?.map((suggestion, index) => (
                <div key={index} className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-blue-800">{suggestion}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Response History */}
      {responses?.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-4">Your Responses</h3>
          <div className="space-y-4">
            {responses?.map((response, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Question {response?.questionId + 1}</span>
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
                      {response?.type}
                    </span>
                    {response?.duration && (
                      <span className="text-sm text-gray-500">
                        {formatTime(response?.duration)}
                      </span>
                    )}
                  </div>
                </div>
                
                {response?.type === 'text' && (
                  <p className="text-gray-700">{response?.content?.substring(0, 100)}...</p>
                )}
                
                {(response?.type === 'audio' || response?.type === 'video') && response?.url && (
                  <div className="flex items-center space-x-2">
                    {response?.type === 'audio' ? (
                      <audio controls className="w-full max-w-md">
                        <source src={response?.url} type="audio/webm" />
                      </audio>
                    ) : (
                      <video controls className="w-64 h-48 rounded">
                        <source src={response?.url} type="video/webm" />
                      </video>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" disabled>
          Previous Question
        </Button>
        <Button onClick={nextQuestion}>
          {currentQuestion === sampleQuestions?.length - 1 ? 'Complete Session' : 'Next Question'}
        </Button>
      </div>
    </div>
  );
};

export default DescriptiveQASection;